import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  Button, 
  Table, 
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Stack,
  Divider,
  Grid,
  useTheme,
  Input,
  Select,
  Option,
  FormControl,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Sheet,
  LinearProgress,
  ColorPaletteProp,
  Textarea,
  FormLabel,
  Badge,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/joy';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaBuilding, 
  FaUser, 
  FaFilter, 
  FaSort,
  FaArrowUp, 
  FaArrowDown,
  FaSearch,
  FaAddressBook,
  FaTruck,
  FaPlus,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaNewspaper,
  FaPaperPlane,
  FaTimes,
  FaEye,
  FaSave,
  FaClone,
  FaBriefcase
} from 'react-icons/fa';
import { useCustomerStore } from '../store/customerStore';
import { customerService } from '../services/customerService';
import { directSupabase } from '../lib/supabaseClient';
import { IconWrapper } from '../components/IconWrapper';
import { useIsMobile } from '../hooks/useIsMobile';
import CustomerCreationForm from '../components/CustomerCreationForm';
import { useCustomer } from '../hooks/useCustomer';
import { Customer } from '../services/customerService';
import { customerExtraDetails, mapCustomerRowToUnified, paymentPreferenceLabel } from '../services/customerMapping';
import { CustomerEditForm } from '../components/CustomerEditForm';
import AccountsTab from '../modules/accounts/components/AccountsTab';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { getGraphClient, isSignedIn, signIn, processTemplateVariables, cleanupRemainingTemplateVariables } from '../utils/graphService';
import { newsletters, type Newsletter } from '../lib/newsletters.service';
import { BLANK_NEWSLETTER_BODY, nextCloneName, newsletterSaveMode } from '../lib/newsletters.model';
import {
  buildNewsletterUnsubscribeUrl,
  buildNewsletterUnsubscribeWidget,
  NEWSLETTER_UNSUBSCRIBE_LINK_VARIABLE,
  NEWSLETTER_UNSUBSCRIBE_WIDGET_VARIABLE
} from '../utils/newsletterUnsubscribe';

// Types
interface Supplier {
  id: string;
  supplier_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  category: string | null;
  notes: string | null;
  created_at: string;
}

interface NewsletterData {
  subject: string;
  content: string;
  recipients: string[];
  excludedEmails: string[];
}

interface NewsletterUnsubscribeEntry {
  email: string;
  source: string;
}

const DirectoryPage: React.FC = () => {
  const theme = useTheme();
  const { customers, loadCustomers, updateLocalCustomer, deleteLocalCustomer } = useCustomerStore();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(0);
  const [accountsCount, setAccountsCount] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<'name' | 'email' | 'phone' | 'company'>('name');
  const [supplierSortColumn, setSupplierSortColumn] = useState<'name' | 'email' | 'phone' | 'category'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [suppliersData, setSuppliersData] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [suppliersError, setSuppliersError] = useState<string | null>(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [createdCustomer, setCreatedCustomer] = useState<Customer | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [suppliersCurrentPage, setSuppliersCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [directoryCustomers, setDirectoryCustomers] = useState<Customer[]>([]);
  const [directoryCustomersLoading, setDirectoryCustomersLoading] = useState(false);
  const [directoryCustomersError, setDirectoryCustomersError] = useState<string | null>(null);
  const [customerTotalCount, setCustomerTotalCount] = useState(0);

  // Newsletter state
  const [newsletterData, setNewsletterData] = useState<NewsletterData>({
    subject: '',
    content: '',
    recipients: [],
    excludedEmails: []
  });
  const [newsletterTemplate, setNewsletterTemplate] = useState<string>('');
  const [newsletterSubject, setNewsletterSubject] = useState<string>('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);
  const [newsletterSuccess, setNewsletterSuccess] = useState<string | null>(null);
  const [showSendNewsletterDialog, setShowSendNewsletterDialog] = useState(false);
  const [newsletterActiveTab, setNewsletterActiveTab] = useState(0);
  const [unsubscribedEmails, setUnsubscribedEmails] = useState<NewsletterUnsubscribeEntry[]>([]);
  const [manualUnsubscribeEmail, setManualUnsubscribeEmail] = useState('');
  const [manualUnsubscribeSaving, setManualUnsubscribeSaving] = useState(false);
  
  const [newsletterItems, setNewsletterItems] = useState<Newsletter[]>([]);
  const [selectedNewsletterId, setSelectedNewsletterId] = useState<string | null>(null);
  const [newsletterName, setNewsletterName] = useState('');
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [createName, setCreateName] = useState('');
  const [savingNewsletter, setSavingNewsletter] = useState(false);
  const [pendingAfterSave, setPendingAfterSave] = useState<'send' | null>(null);
  const selectedNewsletterIdRef = useRef<string | null>(null);
  const newslettersBootstrapped = useRef(false);
  const [originalNewsletterTemplate, setOriginalNewsletterTemplate] = useState<string>('');
  const [originalNewsletterSubject, setOriginalNewsletterSubject] = useState<string>('');
  const [originalNewsletterName, setOriginalNewsletterName] = useState('');
  const hasUnsavedNewsletterChanges = useMemo(
    () => newsletterTemplate !== originalNewsletterTemplate
      || newsletterSubject !== originalNewsletterSubject
      || newsletterName !== originalNewsletterName,
    [newsletterTemplate, originalNewsletterTemplate, newsletterSubject, originalNewsletterSubject, newsletterName, originalNewsletterName]
  );
  // Newsletter template variables
  const newsletterTemplateVariables = [
    { name: 'customerName', description: 'Customer name', sample: 'John Smith' },
    { name: 'customerEmail', description: 'Customer email address', sample: 'john@example.com' },
    { name: 'customerPhone', description: 'Customer phone number', sample: '07700 900000' },
    { name: 'customerCompany', description: 'Customer company name (optional)', sample: 'ABC Installations Ltd' },
    { name: 'customerAddress', description: 'Customer address (optional)', sample: '123 Main Street, London' },
    { name: NEWSLETTER_UNSUBSCRIBE_LINK_VARIABLE, description: 'Direct unsubscribe URL for this recipient', sample: 'https://example.com/newsletter/unsubscribe?email=john%40example.com' },
    { name: NEWSLETTER_UNSUBSCRIBE_WIDGET_VARIABLE, description: 'Pre-built unsubscribe footer widget HTML', sample: '<div>Unsubscribe widget</div>' },
    { name: 'currentDate', description: 'Current date', sample: new Date().toLocaleDateString() },
    { name: 'currentYear', description: 'Current year', sample: new Date().getFullYear().toString() }
  ];

  // Helper function to replace template variables
  const replaceNewsletterTemplateVariables = (template: string): string => {
    const sampleUnsubscribeUrl = 'https://example.com/newsletter/unsubscribe?email=john%40example.com';
    let result = template;
    
    // Replace variables with sample data
    newsletterTemplateVariables.forEach(variable => {
      const regex = new RegExp(`{{${variable.name}}}`, 'g');
      if (variable.name === NEWSLETTER_UNSUBSCRIBE_WIDGET_VARIABLE) {
        result = result.replace(regex, buildNewsletterUnsubscribeWidget(sampleUnsubscribeUrl));
        return;
      }
      if (variable.name === NEWSLETTER_UNSUBSCRIBE_LINK_VARIABLE) {
        result = result.replace(regex, sampleUnsubscribeUrl);
        return;
      }
      result = result.replace(regex, variable.sample || '');
    });
    
    return result;
  };

  useEffect(() => {
    const rawPhone = searchParams.get("phone");

    if (rawPhone) {
      // Trim whitespace and then normalize all phone variations to the 07... format
      const phone = rawPhone.trim();
      let normalizedPhone = phone;

      if (phone.startsWith("+447")) {
        normalizedPhone = phone.replace("+447", "07");
      } else if (phone.startsWith("447")) {
        normalizedPhone = phone.replace("447", "07");
      } else if (phone.startsWith("07")) {
        // Remove spaces from local numbers like '07 123 456 789'
        normalizedPhone = phone.replace(/\s/g, "");
      }

      setSearchQuery(normalizedPhone);
    }
  }, []);

  // Simple fallback to ensure component always renders
  console.log('DirectoryPage component rendering');

  // Helper function to format name as "surname, first name"
  const formatName = (name: string) => {
    if (!name) return '—';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      const lastName = parts[parts.length - 1];
      const firstName = parts.slice(0, -1).join(' ');
      return `${lastName}, ${firstName}`;
    }
    return name;
  };

  // Helper function to get sortable name for sorting (surname first)
  const getSortableName = (name: string) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      // Sort by surname first, then first name
      const lastName = parts[parts.length - 1];
      const firstName = parts.slice(0, -1).join(' ');
      return `${lastName.toLowerCase()} ${firstName.toLowerCase()}`;
    }
    return name.toLowerCase();
  };

  // Helper function to generate Google Maps URL
  const getGoogleMapsUrl = (address: string | null): string | undefined => {
    if (!address) return undefined;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      console.log('Fetching suppliers...');
      setSuppliersLoading(true);
      setSuppliersError(null);
      try {
        const { data, error } = await directSupabase
          .from('suppliers')
          .select('*')
          .order('supplier_name', { ascending: true });
        
        if (error) {
          throw new Error(error.message);
        }
        console.log('Suppliers fetch result:', { data });
        setSuppliersData(data || []);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        setSuppliersError(err instanceof Error ? err.message : 'Failed to fetch suppliers');
      } finally {
        setSuppliersLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  const loadDirectoryCustomers = async () => {
    try {
      setDirectoryCustomersLoading(true);
      setDirectoryCustomersError(null);

      const sortColumnMap: Record<typeof sortColumn, string> = {
        name: 'name',
        email: 'email',
        phone: 'phone_number',
        company: 'organization_name',
      };

      let query = directSupabase
        .from('customers')
        .select('*', { count: 'exact' });

      const trimmedQuery = searchQuery.trim();
      if (trimmedQuery) {
        query = query.or(
          `name.ilike.%${trimmedQuery}%,phone_number.ilike.%${trimmedQuery}%,email.ilike.%${trimmedQuery}%,organization_name.ilike.%${trimmedQuery}%,address.ilike.%${trimmedQuery}%`
        );
      }

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage - 1;

      const { data, error, count } = await query
        .order(sortColumnMap[sortColumn], { ascending: sortDirection === 'asc' })
        .range(start, end);

      if (error) {
        throw error;
      }

      const nextCustomers =
        data?.map((customer: any) => mapCustomerRowToUnified(customer, 'local')) || [];

      setDirectoryCustomers(nextCustomers);
      setCustomerTotalCount(count || 0);
    } catch (error) {
      console.error('Error loading directory customers:', error);
      setDirectoryCustomers([]);
      setCustomerTotalCount(0);
      setDirectoryCustomersError(error instanceof Error ? error.message : 'Failed to load customers');
    } finally {
      setDirectoryCustomersLoading(false);
    }
  };

  // Load newsletter customers only when that tab is opened.
  useEffect(() => {
    if (activeTab === 2 && customers.length === 0) {
      console.log('Loading customers from Supabase for newsletter...');
      loadCustomers();
    }
  }, [activeTab, customers.length, loadCustomers]);

  // Load customer directory rows server-side for the visible page.
  useEffect(() => {
    loadDirectoryCustomers();
  }, [currentPage, searchQuery, sortColumn, sortDirection]);

  // Refresh customers when a new customer is created
  useEffect(() => {
    if (createdCustomer) {
      loadCustomers();
      loadDirectoryCustomers();
    }
  }, [createdCustomer]);

  // Load newsletter items when that tab is opened.
  useEffect(() => {
    if (activeTab === 2) {
      loadNewsletters();
      loadNewsletterUnsubscribes();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 2) {
      setupNewsletterRecipients();
    }
  }, [activeTab, customers, unsubscribedEmails]);

  const applyNewsletter = (item: Newsletter | null) => {
    const name = item?.name || '';
    const subject = item?.subject || '';
    const body = item?.body || BLANK_NEWSLETTER_BODY;
    const id = item?.id || null;
    selectedNewsletterIdRef.current = id;
    setSelectedNewsletterId(id);
    setNewsletterName(name);
    setNewsletterSubject(subject);
    setNewsletterTemplate(body);
    setOriginalNewsletterName(name);
    setOriginalNewsletterSubject(subject);
    setOriginalNewsletterTemplate(body);
  };

  const loadNewsletters = async (preferId?: string) => {
    const showSpinner = !newslettersBootstrapped.current && !preferId;
    try {
      if (showSpinner) setNewsletterLoading(true);
      setNewsletterError(null);
      const { data, error } = await newsletters.list();
      if (error) {
        setNewsletterError(error);
        return;
      }
      const items = data || [];
      setNewsletterItems(items);
      const targetId = preferId ?? selectedNewsletterIdRef.current;
      const match = items.find((item) => item.id === targetId) || null;
      if (match) {
        applyNewsletter(match);
        newslettersBootstrapped.current = true;
        return;
      }
      if (!newslettersBootstrapped.current) {
        const residence = items.find((item) => item.name === 'The Residence Collection');
        applyNewsletter(residence || items[0] || null);
        newslettersBootstrapped.current = true;
      }
    } catch (error) {
      console.error('Error loading newsletters:', error);
      setNewsletterError('Failed to load newsletters');
    } finally {
      if (showSpinner) setNewsletterLoading(false);
    }
  };

  // Setup newsletter recipients from all customers
  const setupNewsletterRecipients = () => {
    const unsubscribed = new Set(unsubscribedEmails.map(entry => entry.email.trim().toLowerCase()));
    const customerEmails = customers
      .filter(customer => customer.email && customer.email.trim() !== '')
      .map(customer => customer.email!.trim().toLowerCase())
      .filter(email => !unsubscribed.has(email))
      .filter((email, index, self) => self.indexOf(email) === index); // Remove duplicates
    
    setNewsletterData(prev => ({
      ...prev,
      recipients: customerEmails,
      excludedEmails: []
    }));
  };

  const loadNewsletterUnsubscribes = async (): Promise<NewsletterUnsubscribeEntry[]> => {
    try {
      const { data, error } = await (directSupabase as any)
        .from('newsletter_unsubscribes')
        .select('email, source')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const map = new Map<string, NewsletterUnsubscribeEntry>();
      ((data || []) as Array<{ email: string | null; source: string | null }>).forEach((row) => {
        const normalized = String(row.email || '').trim().toLowerCase();
        if (!normalized || map.has(normalized)) return;
        map.set(normalized, {
          email: normalized,
          source: String(row.source || 'newsletter_link')
        });
      });

      const uniqueEntries = Array.from(map.values());
      setUnsubscribedEmails(uniqueEntries);
      return uniqueEntries;
    } catch (error) {
      console.error('Failed to load newsletter unsubscribes:', error);
      setUnsubscribedEmails([]);
      return [];
    }
  };

  const handleAddManualUnsubscribe = async () => {
    const normalizedEmail = manualUnsubscribeEmail.trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setNewsletterError('Please enter a valid email address to exclude.');
      return;
    }

    setManualUnsubscribeSaving(true);
    setNewsletterError(null);

    try {
      const payload = {
        email: normalizedEmail,
        normalized_email: normalizedEmail,
        is_active: true,
        source: 'manual_directory',
        unsubscribed_at: new Date().toISOString()
      };

      const { error } = await (directSupabase as any)
        .from('newsletter_unsubscribes')
        .upsert(payload, { onConflict: 'normalized_email' });

      if (error) throw error;

      setManualUnsubscribeEmail('');
      await loadNewsletterUnsubscribes();
      setNewsletterSuccess(`${normalizedEmail} added to exclusions.`);
      setTimeout(() => setNewsletterSuccess(null), 2500);
    } catch (error) {
      console.error('Failed to add manual unsubscribe:', error);
      setNewsletterError('Failed to add exclusion email.');
    } finally {
      setManualUnsubscribeSaving(false);
    }
  };

  // Handle newsletter recipient removal
  const handleRemoveRecipient = (email: string) => {
    setNewsletterData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(recipient => recipient !== email),
      excludedEmails: [...prev.excludedEmails, email]
    }));
  };

  // Handle newsletter recipient restoration
  const handleRestoreRecipient = (email: string) => {
    setNewsletterData(prev => ({
      ...prev,
      recipients: [...prev.recipients, email],
      excludedEmails: prev.excludedEmails.filter(excluded => excluded !== email)
    }));
  };

  // Handle newsletter send (placeholder for now)
  const handleSendNewsletter = async () => {
    try {
      setNewsletterLoading(true);
      setNewsletterError(null);
      setShowSendNewsletterDialog(false);
      
      // Check if user is signed in
      if (!(await isSignedIn())) {
        await signIn();
        if (!(await isSignedIn())) {
          throw new Error("User is not signed in");
        }
      }
      
      const client = await getGraphClient();

      // Refresh unsubscribes just before send to avoid sending to recent opt-outs.
      const latestUnsubscribed = await loadNewsletterUnsubscribes();
      const unsubscribedSet = new Set(latestUnsubscribed.map(entry => entry.email.trim().toLowerCase()));
      
      // Get all customers for template variable processing
      const allCustomers = customers.filter(customer => 
        newsletterData.recipients.includes((customer.email || '').trim().toLowerCase())
      );
      
      // Send personalized newsletter to each recipient
      for (const customer of allCustomers) {
        if (!customer.email) continue;
        const normalizedEmail = customer.email.trim().toLowerCase();
        if (unsubscribedSet.has(normalizedEmail)) continue;
        const unsubscribeLink = buildNewsletterUnsubscribeUrl(normalizedEmail);
        
        // Prepare template variables for this customer
        const templateVariables: Record<string, string> = {
          customerName: customer.name || '',
          customerEmail: normalizedEmail,
          customerPhone: customer.phone || '',
          customerCompany: customer.company || '',
          customerAddress: customer.address || '',
          referralUrl: `${(String(process.env.REACT_APP_PUBLIC_BASE_URL || '').trim() || window.location.origin).replace(/\/+$/g, '')}/refer?source=${encodeURIComponent('newsletter')}`,
          [NEWSLETTER_UNSUBSCRIBE_LINK_VARIABLE]: unsubscribeLink,
          [NEWSLETTER_UNSUBSCRIBE_WIDGET_VARIABLE]: buildNewsletterUnsubscribeWidget(unsubscribeLink),
          currentDate: new Date().toLocaleDateString(),
          currentYear: new Date().getFullYear().toString()
        };
        
        // Process template variables for this customer
        let emailContent = newsletterTemplate;
        let emailSubject = newsletterSubject || 'Newsletter';
        
        // Replace variables in content and subject
        emailContent = processTemplateVariables(emailContent, templateVariables);
        emailSubject = processTemplateVariables(emailSubject, templateVariables);
        
        // Clean up any remaining template variables
        emailContent = cleanupRemainingTemplateVariables(emailContent);
        emailSubject = cleanupRemainingTemplateVariables(emailSubject);
        
        // Ensure proper HTML structure
        if (!emailContent.includes('<html') && !emailContent.includes('<!DOCTYPE')) {
          emailContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="utf-8">
              <title>${emailSubject}</title>
            </head>
            <body>
              ${emailContent}
            </body>
            </html>
          `;
        }
        
        // Create email object for this customer
        const email = {
          message: {
            subject: emailSubject,
            body: {
              contentType: 'HTML',
              content: emailContent
            },
            sender: {
              emailAddress: {
                address: 'info@keystoneglazing.co.uk',
                name: 'Keystone Glazing'
              }
            },
            toRecipients: [
              {
                emailAddress: {
                  address: customer.email
                }
              }
            ]
          },
          saveToSentItems: true
        };
        
        // Send the email to this customer
        await client.api('/me/sendMail').post(email);
      }
      
      console.log('Newsletter sent to:', newsletterData.recipients);
      setNewsletterSuccess(`Newsletter sent to ${newsletterData.recipients.length} recipients!`);
      setTimeout(() => setNewsletterSuccess(null), 3000);
    } catch (error) {
      console.error('Error sending newsletter:', error);
      setNewsletterError('Failed to send newsletter: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleSendNewsletterConfirm = () => {
    if (!selectedNewsletterId || hasUnsavedNewsletterChanges) {
      setPendingAfterSave('send');
      if (newsletterSaveMode(selectedNewsletterId) === 'overwrite') {
        setShowOverwriteDialog(true);
      } else {
        setCreateName(newsletterName);
        setShowCreateDialog(true);
      }
      return;
    }
    setShowSendNewsletterDialog(true);
  };

  // Handle test newsletter send
  const handleTestNewsletter = async () => {
    try {
      setNewsletterLoading(true);
      setNewsletterError(null);
      
      // Check if user is signed in
      if (!(await isSignedIn())) {
        await signIn();
        if (!(await isSignedIn())) {
          throw new Error("User is not signed in");
        }
      }
      
      const client = await getGraphClient();
      const unsubscribeLink = buildNewsletterUnsubscribeUrl('test@example.com');
      
      // Prepare template variables for test email (using sample data)
      const templateVariables: Record<string, string> = {
        customerName: 'Test Customer',
        customerEmail: 'test@example.com',
        customerPhone: '07700 900000',
        customerCompany: 'Test Company Ltd',
        customerAddress: '123 Test Street, London',
        referralUrl: `${(String(process.env.REACT_APP_PUBLIC_BASE_URL || '').trim() || window.location.origin).replace(/\/+$/g, '')}/refer?source=${encodeURIComponent('newsletter')}`,
        [NEWSLETTER_UNSUBSCRIBE_LINK_VARIABLE]: unsubscribeLink,
        [NEWSLETTER_UNSUBSCRIBE_WIDGET_VARIABLE]: buildNewsletterUnsubscribeWidget(unsubscribeLink),
        currentDate: new Date().toLocaleDateString(),
        currentYear: new Date().getFullYear().toString()
      };
      
      // Process template variables for test email
      let emailContent = newsletterTemplate;
      let emailSubject = newsletterSubject || 'Newsletter Test';
      
      // Replace variables in content and subject
      emailContent = processTemplateVariables(emailContent, templateVariables);
      emailSubject = processTemplateVariables(emailSubject, templateVariables);
      
      // Clean up any remaining template variables
      emailContent = cleanupRemainingTemplateVariables(emailContent);
      emailSubject = cleanupRemainingTemplateVariables(emailSubject);
      
      // Ensure proper HTML structure
      if (!emailContent.includes('<html') && !emailContent.includes('<!DOCTYPE')) {
        emailContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>${emailSubject}</title>
          </head>
          <body>
            ${emailContent}
          </body>
          </html>
        `;
      }
      
      // Create email object
      const email = {
        message: {
          subject: emailSubject,
          body: {
            contentType: 'HTML',
            content: emailContent
          },
          sender: {
            emailAddress: {
              address: 'info@keystoneglazing.co.uk',
              name: 'Keystone Glazing'
            }
          },
          toRecipients: [
            {
              emailAddress: {
                address: 'info@keystoneglazing.co.uk'
              }
            }
          ]
        },
        saveToSentItems: true
      };
      
      // Send the email
      await client.api('/me/sendMail').post(email);
      
      console.log('Test newsletter sent to: info@keystoneglazing.co.uk');
      setNewsletterSuccess('Test newsletter sent to info@keystoneglazing.co.uk!');
      setTimeout(() => setNewsletterSuccess(null), 3000);
    } catch (error) {
      console.error('Error sending test newsletter:', error);
      setNewsletterError('Failed to send test newsletter: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Debug logging
  console.log('DirectoryPage render state:', {
    suppliersLoading,
    suppliersError,
    suppliersDataLength: suppliersData.length,
    customersLength: customers.length,
    directoryCustomersLength: directoryCustomers.length
  });

  // Handle customer creation
  const handleCreateCustomer = async (customerData: any) => {
    try {
      // The customer has already been created by the form, so we just need to handle the success
      setCreatedCustomer(customerData);
      setShowCustomerForm(false);
      // Add the new customer to the store and refresh the list
      await loadCustomers();
      await loadDirectoryCustomers();
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleEditCustomer = async (id: string, updates: any) => {
    try {
      await updateLocalCustomer(id, updates);
      setEditingCustomer(null);
      // Refresh the customer list to ensure updated data is loaded
      await loadCustomers();
      await loadDirectoryCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  };

  const handleDeleteCustomer = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this customer? This action cannot be undone.')) {
      try {
        await deleteLocalCustomer(id);
        await loadDirectoryCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  // Get unique categories from suppliers
  const supplierCategories = useMemo(() => {
    const categories = suppliersData
      .map(s => s.category)
      .filter((cat): cat is string => cat !== null && cat !== '');
    return ['all', ...Array.from(new Set(categories))];
  }, [suppliersData]);

  // Pagination logic for customers
  const totalCustomers = customerTotalCount;
  const totalCustomerPages = Math.ceil(totalCustomers / itemsPerPage);
  const startCustomerIndex = (currentPage - 1) * itemsPerPage;
  const endCustomerIndex = startCustomerIndex + itemsPerPage;
  const paginatedCustomers = directoryCustomers;

  // Filter and sort suppliers
  const filteredAndSortedSuppliers = useMemo(() => {
    let result = suppliersData;
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      result = result.filter(supplier => {
        return (
          supplier.supplier_name.toLowerCase().includes(q) ||
          (supplier.contact_name && supplier.contact_name.toLowerCase().includes(q)) ||
          (supplier.phone && supplier.phone.includes(q)) ||
          (supplier.email && supplier.email.toLowerCase().includes(q)) ||
          (supplier.category && supplier.category.toLowerCase().includes(q)) ||
          (supplier.address && supplier.address.toLowerCase().includes(q))
        );
      });
    }

    // Apply category filter
    if (suppliersCurrentPage === 1) { // Only apply category filter on the first page
      if (supplierCategories.length > 1) { // If there's a category filter applied
        result = result.filter(supplier => supplier.category === supplierCategories[1]); // Filter by the selected category
      }
    }

    // Sort
    return result.slice().sort((a, b) => {
      let aValue: any, bValue: any;
      switch (supplierSortColumn) {
        case 'name':
          aValue = a.supplier_name.toLowerCase();
          bValue = b.supplier_name.toLowerCase();
          break;
        case 'phone':
          aValue = a.phone || '';
          bValue = b.phone || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        default:
          aValue = '';
          bValue = '';
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [suppliersData, searchQuery, supplierSortColumn, sortDirection, suppliersCurrentPage, supplierCategories]);

  // Pagination logic for suppliers
  const totalSuppliers = filteredAndSortedSuppliers.length;
  const totalSupplierPages = Math.ceil(totalSuppliers / itemsPerPage);
  const startSupplierIndex = (suppliersCurrentPage - 1) * itemsPerPage;
  const endSupplierIndex = startSupplierIndex + itemsPerPage;
  const paginatedSuppliers = filteredAndSortedSuppliers.slice(startSupplierIndex, endSupplierIndex);

  // Sort handler
  const handleSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Supplier sort handler
  const handleSupplierSort = (column: typeof supplierSortColumn) => {
    if (supplierSortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSupplierSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortColumn, sortDirection]);

  // Filter/search bar
  const renderFilterBar = () => (
    <Box sx={{ 
      mb: 3, 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' }, 
      gap: { xs: 1.5, sm: 2 },
      '& .MuiFormControl-root': {
        width: { xs: '100%', sm: 'auto' },
        minWidth: { xs: '100%', sm: 200 },
      },
      '& .MuiInput-root, & .MuiSelect-root': {
        py: { xs: 1.5, sm: 1 },
      }
    }}>
      <FormControl>
        <Input
          placeholder="Search contacts..."
          startDecorator={<IconWrapper icon={FaSearch} />}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          size={isMobile ? "lg" : "md"}
        />
      </FormControl>
      
      {activeTab === 1 && (
        <FormControl>
          <Select
            placeholder="Category"
            value={supplierCategories[1]} // Only show category filter if it's not 'all'
            onChange={(_, value) => {
              if (value === 'all') {
                setSuppliersCurrentPage(1); // Reset to first page
              } else {
                setSuppliersCurrentPage(1); // Reset to first page and apply category filter
              }
            }}
            size={isMobile ? "lg" : "md"}
          >
            {supplierCategories.map(cat => (
              <Option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </Option>
            ))}
          </Select>
        </FormControl>
      )}
      
      <FormControl>
        <Select
          placeholder="Sort by"
          value={sortColumn}
          onChange={(_, value) => setSortColumn(value as typeof sortColumn)}
          endDecorator={
            <IconButton 
              variant="plain" 
              color="neutral" 
              onClick={() => handleSort(sortColumn)}
              size={isMobile ? "lg" : "sm"}
            >
              <IconWrapper icon={FaSort} />
              {sortDirection === 'asc' ? '↑' : '↓'}
            </IconButton>
          }
          size={isMobile ? "lg" : "md"}
        >
          <Option value="name">Name</Option>
          <Option value="phone">Phone</Option>
          <Option value="email">Email</Option>
          <Option value="company">Organization</Option>
        </Select>
      </FormControl>
    </Box>
  );

  // Mobile Customer Card
  const renderMobileCustomerCard = (customer: Customer) => (
    <Card 
      key={customer.id}
      sx={{ 
        width: '100%', 
        transition: 'transform 0.2s ease, box-shadow 0.2s ease', 
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 'md' }
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 1.5,
          gap: 1
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Link
              to={`/customers/${encodeURIComponent(customer.name)}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography 
                level="title-md" 
                sx={{ 
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'primary.600',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  '&:hover': {
                    color: 'primary.700',
                  }
                }}
              >
                {formatName(customer.name)}
              </Typography>
            </Link>
            <Typography 
              level="body-sm" 
              sx={{ 
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {customer.company}
            </Typography>
          </Box>
          
          {/* Edit/Delete buttons */}
          {
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit customer">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="primary"
                  onClick={async () => {
                    // Fetch the latest customer data from the database to ensure we have correct organization info
                    try {
                      const freshCustomer = await customerService.getLocalCustomerById(customer.id);
                      if (freshCustomer) {
                        setEditingCustomer(freshCustomer);
                      } else {
                        // Fallback to store customer if direct fetch fails
                        setEditingCustomer(customer);
                      }
                    } catch (error) {
                      console.error('Error fetching customer for edit:', error);
                      // Fallback to store customer if fetch fails
                      setEditingCustomer(customer);
                    }
                  }}
                >
                  <IconWrapper icon={FaEdit} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete customer">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => handleDeleteCustomer(customer.id)}
                >
                  <IconWrapper icon={FaTrash} />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={2}>
          <Grid xs={12}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              mb: 1.5,
              p: 1,
              bgcolor: 'background.level1',
              borderRadius: 'sm'
            }}>
              <Typography level="body-sm" fontWeight="lg">Contact Information</Typography>
              <Stack spacing={1}>
                {customer.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconWrapper icon={FaPhone} />
                    <Box
                      component="a"
                      href={`tel:${customer.phone}`}
                      sx={{
                        color: 'primary.600',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: 'primary.700',
                        }
                      }}
                    >
                      {customer.phone}
                    </Box>
                  </Box>
                )}
                {customer.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconWrapper icon={FaEnvelope} />
                    <Box
                      component="a"
                      href={`mailto:${customer.email}`}
                      sx={{
                        color: 'primary.600',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: 'primary.700',
                        }
                      }}
                    >
                      {customer.email}
                    </Box>
                  </Box>
                )}
                {customerExtraDetails(customer).map((item) => (
                  <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500', minWidth: 92 }}>
                      {item.label}
                    </Typography>
                    {item.href ? (
                      <Box
                        component="a"
                        href={item.href}
                        sx={{
                          color: 'primary.600',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': { color: 'primary.700' },
                        }}
                      >
                        {item.value}
                      </Box>
                    ) : (
                      <Typography level="body-sm">{item.value}</Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid xs={12}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              mb: 1.5,
              p: 1,
              bgcolor: 'background.level1',
              borderRadius: 'sm'
            }}>
              <Typography level="body-sm" fontWeight="lg">Address</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <IconWrapper icon={FaMapMarkerAlt} />
                {customer.address && getGoogleMapsUrl(customer.address) ? (
                  <Box
                    component="a"
                    href={getGoogleMapsUrl(customer.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.600',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        color: 'primary.700',
                      }
                    }}
                  >
                    {customer.address}
                  </Box>
                ) : (
                  <Typography level="body-sm">
                    {customer.address || '—'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );

  // Mobile Supplier Card
  const renderMobileSupplierCard = (supplier: Supplier) => (
    <Card 
      key={supplier.id}
      sx={{ 
        width: '100%', 
        transition: 'transform 0.2s ease, box-shadow 0.2s ease', 
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 'md' }
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 1.5,
          gap: 1
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              level="title-md" 
              sx={{ 
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {supplier.supplier_name}
            </Typography>
            {supplier.category && (
              <Chip size="sm" variant="soft" color="primary" sx={{ mb: 1 }}>
                {supplier.category}
              </Chip>
            )}
            {supplier.contact_name && (
              <Typography 
                level="body-sm" 
                sx={{ 
                  mb: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                Contact: {supplier.contact_name}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={2}>
          <Grid xs={12}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              mb: 1.5,
              p: 1,
              bgcolor: 'background.level1',
              borderRadius: 'sm'
            }}>
              <Typography level="body-sm" fontWeight="lg">Contact Information</Typography>
              <Stack spacing={1}>
                {supplier.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconWrapper icon={FaPhone} />
                    <Box
                      component="a"
                      href={`tel:${supplier.phone}`}
                      sx={{
                        color: 'primary.600',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: 'primary.700',
                        }
                      }}
                    >
                      {supplier.phone}
                    </Box>
                  </Box>
                )}
                {supplier.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconWrapper icon={FaEnvelope} />
                    <Box
                      component="a"
                      href={`mailto:${supplier.email}`}
                      sx={{
                        color: 'primary.600',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: 'primary.700',
                        }
                      }}
                    >
                      {supplier.email}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
          </Grid>

          {supplier.address && (
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1, 
                mb: 1.5,
                p: 1,
                bgcolor: 'background.level1',
                borderRadius: 'sm'
              }}>
                <Typography level="body-sm" fontWeight="lg">Address</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <IconWrapper icon={FaMapMarkerAlt} />
                  {supplier.address && getGoogleMapsUrl(supplier.address) ? (
                    <Box
                      component="a"
                      href={getGoogleMapsUrl(supplier.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.600',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: 'primary.700',
                        }
                      }}
                    >
                      {supplier.address}
                    </Box>
                  ) : (
                    <Typography level="body-sm">
                      {supplier.address || '—'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}

          {supplier.notes && (
            <Grid xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1, 
                mb: 1.5,
                p: 1,
                bgcolor: 'background.level1',
                borderRadius: 'sm'
              }}>
                <Typography level="body-sm" fontWeight="lg">Notes</Typography>
                <Typography level="body-sm">
                  {supplier.notes}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Card>
  );

  // Desktop Customer Table
  const renderDesktopCustomerTable = () => (
    <>
      <Table stickyHeader>
        <thead>
          <tr>
            <th style={{ width: '20%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography level="body-sm" fontWeight="lg">Name</Typography>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => handleSort('name')}
                  sx={{ ml: 0.5 }}
                >
                  {sortColumn === 'name' ? (
                    sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                  ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
                </IconButton>
              </Box>
            </th>
            <th style={{ width: '15%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography level="body-sm" fontWeight="lg">Organization</Typography>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => handleSort('company')}
                  sx={{ ml: 0.5 }}
                >
                  {sortColumn === 'company' ? (
                    sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                  ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
                </IconButton>
              </Box>
            </th>
            <th style={{ width: '15%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography level="body-sm" fontWeight="lg">Phone</Typography>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => handleSort('phone')}
                  sx={{ ml: 0.5 }}
                >
                  {sortColumn === 'phone' ? (
                    sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                  ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
                </IconButton>
              </Box>
            </th>
            <th style={{ width: '20%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography level="body-sm" fontWeight="lg">Email</Typography>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => handleSort('email')}
                  sx={{ ml: 0.5 }}
                >
                  {sortColumn === 'email' ? (
                    sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                  ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
                </IconButton>
              </Box>
            </th>
            <th style={{ width: '20%' }}>
              <Typography level="body-sm" fontWeight="lg">Address</Typography>
            </th>
            <th style={{ width: '10%' }}>
              <Typography level="body-sm" fontWeight="lg">Actions</Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedCustomers.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center' }}>
                <Typography level="body-sm">No customers found.</Typography>
              </td>
            </tr>
          ) : (
            paginatedCustomers.map((customer) => (
              <tr key={customer.id}>
                <td>
                  <Link
                    to={`/customers/${encodeURIComponent(customer.name)}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography 
                      level="body-md" 
                      fontWeight="md"
                      sx={{
                        color: 'primary.600',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': {
                          color: 'primary.700',
                        }
                      }}
                    >
                      {formatName(customer.name)}
                    </Typography>
                  </Link>
                </td>
                <td>
                  <Typography level="body-sm">
                    {customer.company}
                  </Typography>
                </td>
                <td>
                  <Stack spacing={0.25}>
                    {customer.phone ? (
                      <Box
                        component="a"
                        href={`tel:${customer.phone}`}
                        sx={{
                          color: 'primary.600',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': {
                            color: 'primary.700',
                          }
                        }}
                      >
                        {customer.phone}
                      </Box>
                    ) : (
                      '—'
                    )}
                    {customer.landline && (
                      <Box
                        component="a"
                        href={`tel:${customer.landline}`}
                        sx={{
                          color: 'neutral.600',
                          fontSize: '0.75rem',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                        }}
                      >
                        {customer.landline}
                      </Box>
                    )}
                  </Stack>
                </td>
                <td>
                  <Stack spacing={0.25}>
                    {customer.email ? (
                      <Box
                        component="a"
                        href={`mailto:${customer.email}`}
                        sx={{
                          color: 'primary.600',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': {
                            color: 'primary.700',
                          }
                        }}
                      >
                        {customer.email}
                      </Box>
                    ) : (
                      '—'
                    )}
                    {customer.billingEmail && (
                      <Box
                        component="a"
                        href={`mailto:${customer.billingEmail}`}
                        sx={{
                          color: 'neutral.600',
                          fontSize: '0.75rem',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                        }}
                      >
                        {customer.billingEmail}
                      </Box>
                    )}
                  </Stack>
                </td>
                <td>
                  <Stack spacing={0.25}>
                    {customer.address && getGoogleMapsUrl(customer.address) ? (
                      <Box
                        component="a"
                        href={getGoogleMapsUrl(customer.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.600',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': {
                            color: 'primary.700',
                          }
                        }}
                      >
                        {customer.address}
                      </Box>
                    ) : (
                      customer.address || '—'
                    )}
                    {customer.billingContact && (
                      <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                        {customer.billingContact}
                      </Typography>
                    )}
                    {customer.paymentPreference && (
                      <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                        {paymentPreferenceLabel(customer.paymentPreference)}
                      </Typography>
                    )}
                    {customer.vatNumber && (
                      <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                        VAT {customer.vatNumber}
                      </Typography>
                    )}
                  </Stack>
                </td>
                <td>
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="primary"
                      onClick={async () => {
                        // Fetch the latest customer data from the database to ensure we have correct organization info
                        try {
                          const freshCustomer = await customerService.getLocalCustomerById(customer.id);
                          if (freshCustomer) {
                            setEditingCustomer(freshCustomer);
                          } else {
                            // Fallback to store customer if direct fetch fails
                            setEditingCustomer(customer);
                          }
                        } catch (error) {
                          console.error('Error fetching customer for edit:', error);
                          // Fallback to store customer if fetch fails
                          setEditingCustomer(customer);
                        }
                      }}
                      sx={{ minWidth: 'auto' }}
                    >
                      <IconWrapper icon={FaEdit} />
                    </IconButton>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      
      {/* Footer with customer count and pagination for desktop */}
      {totalCustomers > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mt: 2, 
          px: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          {/* Customer count */}
          <Typography level="body-sm" color="neutral">
            Showing {startCustomerIndex + 1}-{Math.min(endCustomerIndex, totalCustomers)} of {totalCustomers} customers
          </Typography>
          
          {/* Pagination controls */}
          {totalCustomerPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="sm"
                variant="plain"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <IconWrapper icon={FaChevronLeft} />
              </IconButton>
              
              <Typography level="body-sm">
                Page {currentPage} of {totalCustomerPages}
              </Typography>
              
              <IconButton
                size="sm"
                variant="plain"
                disabled={currentPage === totalCustomerPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <IconWrapper icon={FaChevronRight} />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  // Desktop Supplier Table
  const renderDesktopSupplierTable = () => (
    <>
      <Table stickyHeader>
        <thead>
          <tr>
            <th style={{ width: '20%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography level="body-sm" fontWeight="lg">Supplier Name</Typography>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => handleSupplierSort('name')}
                  sx={{ ml: 0.5 }}
                >
                  {supplierSortColumn === 'name' ? (
                    sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                  ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
                </IconButton>
              </Box>
          </th>
          <th style={{ width: '15%' }}>
            <Typography level="body-sm" fontWeight="lg">Category</Typography>
          </th>
          <th style={{ width: '15%' }}>
            <Typography level="body-sm" fontWeight="lg">Contact Person</Typography>
          </th>
          <th style={{ width: '15%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography level="body-sm" fontWeight="lg">Phone</Typography>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => handleSupplierSort('phone')}
                sx={{ ml: 0.5 }}
              >
                {supplierSortColumn === 'phone' ? (
                  sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
              </IconButton>
            </Box>
          </th>
          <th style={{ width: '20%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography level="body-sm" fontWeight="lg">Email</Typography>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => handleSupplierSort('email')}
                sx={{ ml: 0.5 }}
              >
                {supplierSortColumn === 'email' ? (
                  sortDirection === 'asc' ? <IconWrapper icon={FaArrowUp} /> : <IconWrapper icon={FaArrowDown} />
                ) : <IconWrapper icon={FaArrowUp} style={{ opacity: 0.3 }} />}
              </IconButton>
            </Box>
          </th>
          <th style={{ width: '15%' }}>
            <Typography level="body-sm" fontWeight="lg">Address</Typography>
          </th>
        </tr>
      </thead>
      <tbody>
        {paginatedSuppliers.length === 0 ? (
          <tr>
            <td colSpan={6} style={{ textAlign: 'center' }}>
              <Typography level="body-sm">No suppliers found.</Typography>
            </td>
          </tr>
        ) : (
          paginatedSuppliers.map((supplier) => (
            <tr key={supplier.id}>
              <td>
                <Typography level="body-md" fontWeight="md">
                  {formatName(supplier.supplier_name)}
                </Typography>
              </td>
              <td>
                {supplier.category ? (
                  <Chip size="sm" variant="soft" color="primary">
                    {supplier.category}
                  </Chip>
                ) : (
                  '—'
                )}
              </td>
              <td>
                <Typography level="body-sm">
                  {supplier.contact_name || '—'}
                </Typography>
              </td>
              <td>
                {supplier.phone ? (
                  <Box
                    component="a"
                    href={`tel:${supplier.phone}`}
                    sx={{
                      color: 'primary.600',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        color: 'primary.700',
                      }
                    }}
                  >
                    {supplier.phone}
                  </Box>
                ) : (
                  '—'
                )}
              </td>
              <td>
                {supplier.email ? (
                  <Box
                    component="a"
                    href={`mailto:${supplier.email}`}
                    sx={{
                      color: 'primary.600',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        color: 'primary.700',
                      }
                    }}
                  >
                    {supplier.email}
                  </Box>
                ) : (
                  '—'
                )}
              </td>
              <td>
                {supplier.address && getGoogleMapsUrl(supplier.address) ? (
                  <Box
                    component="a"
                    href={getGoogleMapsUrl(supplier.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.600',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        color: 'primary.700',
                      }
                    }}
                  >
                    {supplier.address}
                  </Box>
                ) : (
                  supplier.address || '—'
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
    
    {/* Footer with supplier count and pagination for desktop */}
    {totalSuppliers > 0 && (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mt: 2, 
        px: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        pt: 2
      }}>
        {/* Supplier count */}
        <Typography level="body-sm" color="neutral">
          Showing {startSupplierIndex + 1}-{Math.min(endSupplierIndex, totalSuppliers)} of {totalSuppliers} suppliers
        </Typography>
        
        {/* Pagination controls */}
        {totalSupplierPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="sm"
              variant="plain"
              disabled={suppliersCurrentPage === 1}
              onClick={() => setSuppliersCurrentPage(suppliersCurrentPage - 1)}
            >
              <IconWrapper icon={FaChevronLeft} />
            </IconButton>
            
            <Typography level="body-sm">
              Page {suppliersCurrentPage} of {totalSupplierPages}
            </Typography>
            
            <IconButton
              size="sm"
              variant="plain"
              disabled={suppliersCurrentPage === totalSupplierPages}
              onClick={() => setSuppliersCurrentPage(suppliersCurrentPage + 1)}
            >
              <IconWrapper icon={FaChevronRight} />
            </IconButton>
          </Box>
        )}
      </Box>
    )}
  </>
  );

  const confirmDiscardNewsletterChanges = () => {
    if (!hasUnsavedNewsletterChanges) return true;
    return window.confirm('You have unsaved changes. Leave this newsletter without saving?');
  };

  const handleNewsletterSelection = (newsletterId: string | null) => {
    if (newsletterId === selectedNewsletterId) return;
    if (!confirmDiscardNewsletterChanges()) return;
    if (!newsletterId) {
      applyNewsletter(null);
      return;
    }
    const selected = newsletterItems.find((item) => item.id === newsletterId);
    if (selected) applyNewsletter(selected);
  };

  const finishNewsletterWrite = async (saved: Newsletter | null, successMessage: string) => {
    if (!saved) return;
    setNewsletterSuccess(successMessage);
    await loadNewsletters(saved.id);
    const shouldSend = pendingAfterSave === 'send';
    setPendingAfterSave(null);
    if (shouldSend) setShowSendNewsletterDialog(true);
    setTimeout(() => setNewsletterSuccess(null), 3000);
  };

  const handleSaveClick = () => {
    if (newsletterSaveMode(selectedNewsletterId) === 'overwrite') {
      setPendingAfterSave(null);
      setShowOverwriteDialog(true);
      return;
    }
    setPendingAfterSave(null);
    setCreateName(newsletterName);
    setShowCreateDialog(true);
  };

  const handleOverwriteNewsletter = async () => {
    if (!selectedNewsletterId) return;
    if (newsletters.nameTaken(newsletterItems, newsletterName, selectedNewsletterId)) {
      setNewsletterError('A newsletter with that name already exists. Clone it if this should be a new item.');
      return;
    }
    setSavingNewsletter(true);
    setNewsletterError(null);
    const { data, error } = await newsletters.update(selectedNewsletterId, {
      name: newsletterName,
      subject: newsletterSubject,
      body: newsletterTemplate,
    });
    setSavingNewsletter(false);
    if (error || !data) {
      setNewsletterError(error || 'Failed to save newsletter');
      return;
    }
    setShowOverwriteDialog(false);
    await finishNewsletterWrite(data, `Saved ${data.name}.`);
  };

  const handleCreateNewsletter = async () => {
    const name = createName.trim();
    if (!name) {
      setNewsletterError('Enter a newsletter name.');
      return;
    }
    if (newsletters.nameTaken(newsletterItems, name)) {
      setNewsletterError('A newsletter with that name already exists.');
      return;
    }
    setSavingNewsletter(true);
    setNewsletterError(null);
    const { data, error } = await newsletters.create({
      name,
      subject: newsletterSubject,
      body: newsletterTemplate,
    });
    setSavingNewsletter(false);
    if (error || !data) {
      setNewsletterError(error || 'Failed to create newsletter');
      return;
    }
    setShowCreateDialog(false);
    setCreateName('');
    await finishNewsletterWrite(data, `Created ${data.name}.`);
  };

  const openCloneDialog = () => {
    setCloneName(nextCloneName(newsletterItems.map((item) => item.name), newsletterName || 'Newsletter'));
    setShowCloneDialog(true);
    setShowOverwriteDialog(false);
  };

  const handleCloneNewsletter = async () => {
    const name = cloneName.trim();
    if (!name) {
      setNewsletterError('Enter a name for the cloned newsletter.');
      return;
    }
    if (newsletters.nameTaken(newsletterItems, name)) {
      setNewsletterError('A newsletter with that name already exists.');
      return;
    }
    setSavingNewsletter(true);
    setNewsletterError(null);
    const { data, error } = await newsletters.create({
      name,
      subject: newsletterSubject,
      body: newsletterTemplate,
    });
    setSavingNewsletter(false);
    if (error || !data) {
      setNewsletterError(error || 'Failed to clone newsletter');
      return;
    }
    setShowCloneDialog(false);
    setCloneName('');
    setPendingAfterSave(null);
    await finishNewsletterWrite(data, `Cloned as ${data.name}. The original was left unchanged.`);
  };

  const handleDeleteNewsletter = async () => {
    if (!selectedNewsletterId) {
      if (!confirmDiscardNewsletterChanges()) return;
      applyNewsletter(newsletterItems[0] || null);
      return;
    }
    const current = newsletterItems.find((item) => item.id === selectedNewsletterId);
    if (!window.confirm(`Delete ${current?.name || 'this newsletter'}?`)) return;
    setNewsletterError(null);
    const { error } = await newsletters.remove(selectedNewsletterId);
    if (error) {
      setNewsletterError(error);
      return;
    }
    selectedNewsletterIdRef.current = null;
    newslettersBootstrapped.current = false;
    setNewsletterSuccess('Newsletter deleted.');
    await loadNewsletters();
    setTimeout(() => setNewsletterSuccess(null), 3000);
  };

  const handleNewNewsletter = () => {
    if (!confirmDiscardNewsletterChanges()) return;
    applyNewsletter(null);
  };

  // Loading state
  if (suppliersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  // Error state
  if (suppliersError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="danger">{suppliersError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 3,
          pt: { xs: 0, md: 2 }
        }}
      >
        <Typography level="h2" sx={{ fontSize: { xs: 'xl', md: 'h2' }, color: '#111' }}>
          Directory
        </Typography>
        </Box>
      {/* Customer Creation Section */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="h4" sx={{ color: '#111' }}>
              Add New Customer
            </Typography>
            <Button
              variant={showCustomerForm ? "outlined" : "solid"}
              startDecorator={<IconWrapper icon={FaPlus} />}
              onClick={() => {
                setShowCustomerForm(!showCustomerForm);
              }}
              size="sm"
            >
              {showCustomerForm ? 'Cancel' : 'Add Customer'}
            </Button>
          </Box>
          
          {showCustomerForm && (
            <CustomerCreationForm
              onCustomerCreated={handleCreateCustomer}
            />
          )}
          
          {createdCustomer && (
            <Alert color="success" sx={{ mt: 2 }}>
              Customer "{createdCustomer.name}" created successfully!
            </Alert>
          )}
        </Box>
      </Card>

      <Tabs 
        value={activeTab} 
        onChange={(_, value) => setActiveTab(value as number)}
        sx={{ mb: 3 }}
      >
        <TabList>
          <Tab value={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconWrapper icon={FaAddressBook} />
              <Typography>Customers ({totalCustomers})</Typography>
            </Box>
          </Tab>
          <Tab value={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconWrapper icon={FaTruck} />
              <Typography>Suppliers ({suppliersData.length})</Typography>
            </Box>
          </Tab>
          {/* Explicit values: Accounts sits before Newsletter but keeps panel 3; Newsletter keeps panel 2 */}
          <Tab value={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconWrapper icon={FaBriefcase} />
              <Typography>Accounts{accountsCount != null ? ` (${accountsCount})` : ''}</Typography>
            </Box>
          </Tab>
          <Tab value={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconWrapper icon={FaNewspaper} />
              <Typography>Newsletter</Typography>
            </Box>
          </Tab>

        </TabList>

        {activeTab !== 3 && renderFilterBar()}

        <TabPanel value={0} sx={{ p: 0 }}>
          <Box>
            {directoryCustomersError && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {directoryCustomersError}
              </Alert>
            )}

            {directoryCustomersLoading && paginatedCustomers.length === 0 ? (
              <Card>
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              </Card>
            ) : isMobile ? (
              <Stack spacing={2}>
                {paginatedCustomers.map(renderMobileCustomerCard)}
                {paginatedCustomers.length === 0 && (
                  <Card>
                    <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                      <Typography level="body-lg" sx={{ mb: 2 }}>No customers match your filters</Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setSearchQuery('')}
                        size={isMobile ? "lg" : "md"}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' },
                          py: { xs: 1.5, sm: 1 }
                        }}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  </Card>
                )}
              </Stack>
            ) : (
              renderDesktopCustomerTable()
            )}
            
            {/* Footer with customer count and pagination for mobile */}
            {totalCustomers > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1,
                mt: 2, 
                px: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                pt: 2
              }}>
                {/* Customer count */}
                <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center' }}>
                  Showing {startCustomerIndex + 1}-{Math.min(endCustomerIndex, totalCustomers)} of {totalCustomers} customers
                </Typography>
                
                {/* Pagination controls */}
                {totalCustomerPages > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <IconButton
                      size="sm"
                      variant="plain"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <IconWrapper icon={FaChevronLeft} />
                    </IconButton>
                    
                    <Typography level="body-sm">
                      Page {currentPage} of {totalCustomerPages}
                    </Typography>
                    
                    <IconButton
                      size="sm"
                      variant="plain"
                      disabled={currentPage === totalCustomerPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <IconWrapper icon={FaChevronRight} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={1} sx={{ p: 0 }}>
          <Box>
            {isMobile ? (
              <Stack spacing={2}>
                {paginatedSuppliers.map(renderMobileSupplierCard)}
                {paginatedSuppliers.length === 0 && (
                  <Card>
                    <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                      <Typography level="body-lg" sx={{ mb: 2 }}>No suppliers match your filters</Typography>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSearchQuery('');
                          setSuppliersCurrentPage(1); // Reset to first page
                        }}
                        size={isMobile ? "lg" : "md"}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' },
                          py: { xs: 1.5, sm: 1 }
                        }}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  </Card>
                )}
              </Stack>
            ) : (
              renderDesktopSupplierTable()
            )}
            
            {/* Footer with supplier count and pagination for mobile */}
            {totalSuppliers > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1,
                mt: 2, 
                px: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                pt: 2
              }}>
                {/* Supplier count */}
                <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center' }}>
                  Showing {startSupplierIndex + 1}-{Math.min(endSupplierIndex, totalSuppliers)} of {totalSuppliers} suppliers
                </Typography>
                
                {/* Pagination controls */}
                {totalSupplierPages > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <IconButton
                      size="sm"
                      variant="plain"
                      disabled={suppliersCurrentPage === 1}
                      onClick={() => setSuppliersCurrentPage(suppliersCurrentPage - 1)}
                    >
                      <IconWrapper icon={FaChevronLeft} />
                    </IconButton>
                    
                    <Typography level="body-sm">
                      Page {suppliersCurrentPage} of {totalSupplierPages}
                    </Typography>
                    
                    <IconButton
                      size="sm"
                      variant="plain"
                      disabled={suppliersCurrentPage === totalSupplierPages}
                      onClick={() => setSuppliersCurrentPage(suppliersCurrentPage + 1)}
                    >
                      <IconWrapper icon={FaChevronRight} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={2} sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Newsletter Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography level="h4" sx={{ color: '#111' }}>
                Newsletter
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startDecorator={<IconWrapper icon={FaEnvelope} />}
                  onClick={handleTestNewsletter}
                  disabled={newsletterLoading}
                  size="sm"
                >
                  Test Email
                </Button>
                <Button
                  variant="solid"
                  startDecorator={<IconWrapper icon={FaPaperPlane} />}
                  onClick={handleSendNewsletterConfirm}
                  disabled={newsletterLoading || newsletterData.recipients.length === 0}
                  size="sm"
                >
                  Send Newsletter ({newsletterData.recipients.length} recipients)
                </Button>
              </Stack>
            </Box>

            {/* Success/Error Messages */}
            {newsletterSuccess && (
              <Alert color="success" sx={{ mb: 2 }}>
                {newsletterSuccess}
              </Alert>
            )}
            {newsletterError && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {newsletterError}
              </Alert>
            )}

            {/* Loading State */}
            {newsletterLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size="lg" />
              </Box>
            )}

            {!newsletterLoading && (
              <Grid container spacing={3}>
                {/* Newsletter Editor */}
                <Grid xs={12} md={8}>
                  <Card>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                        <Typography level="title-lg">
                          Newsletter Content
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={handleNewNewsletter}
                            startDecorator={<IconWrapper icon={FaPlus} />}
                          >
                            New
                          </Button>
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={openCloneDialog}
                            startDecorator={<IconWrapper icon={FaClone} />}
                          >
                            Clone
                          </Button>
                          <Button
                            variant="solid"
                            size="sm"
                            onClick={handleSaveClick}
                            startDecorator={<IconWrapper icon={FaSave} />}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            color="danger"
                            size="sm"
                            onClick={handleDeleteNewsletter}
                            startDecorator={<IconWrapper icon={FaTrash} />}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Box>
                      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                        Save overwrites this newsletter. Clone it first if you want a separate one.
                      </Typography>

                      <FormControl sx={{ mb: 2, width: '100%' }}>
                        <FormLabel>Newsletter</FormLabel>
                        <Select
                          value={selectedNewsletterId || 'new'}
                          onChange={(_, value) => handleNewsletterSelection(!value || value === 'new' ? null : value)}
                          placeholder={newsletterItems.length === 0 ? 'No newsletters yet' : 'Select a newsletter'}
                          size="md"
                        >
                          {!selectedNewsletterId && <Option value="new">New newsletter</Option>}
                          {newsletterItems.map((item) => (
                            <Option key={item.id} value={item.id}>
                              {item.name}
                            </Option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl sx={{ mb: 2, width: '100%' }}>
                        <FormLabel>Name</FormLabel>
                        <Input
                          value={newsletterName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewsletterName(e.target.value)}
                          placeholder="Endurance Doors"
                          size="md"
                        />
                      </FormControl>
                      
                      {/* Subject Line */}
                      <FormControl sx={{ mb: 2, width: '100%' }}>
                        <FormLabel>Subject Line</FormLabel>
                        <Input
                          value={newsletterSubject}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewsletterSubject(e.target.value)}
                          placeholder="Enter newsletter subject..."
                          size="md"
                        />
                      </FormControl>

                      {/* Newsletter Tabs */}
                      <Tabs
                        value={newsletterActiveTab}
                        onChange={(_, newValue) => setNewsletterActiveTab(newValue as number)}
                        sx={{ mb: 2 }}
                      >
                        <TabList>
                          <Tab>Design</Tab>
                          <Tab>Source</Tab>
                          <Tab>Template Variables</Tab>
                          <Tab>Preview</Tab>
                        </TabList>
                        
                        <TabPanel value={0}>
                          {/* TinyMCE WYSIWYG Editor */}
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                            <TinyMCEEditor
                              tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                              licenseKey={'gpl'}
                              value={newsletterTemplate}
                              onEditorChange={(content: string) => setNewsletterTemplate(content)}
                              init={{
                                height: 600,
                                menubar: false,
                                plugins: ['link', 'lists', 'table', 'code'],
                                toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link table | code',
                                // Ensure TinyMCE loads all assets (plugins/skins) from our self-hosted folder
                                base_url: '/tinymce',
                                suffix: '.min',
                                valid_elements: '*[*]',
                                // Keep raw braces and avoid cleanup that might remove variables
                                entity_encoding: 'raw',
                                verify_html: false,
                                convert_urls: false,
                                remove_script_host: false,
                                relative_urls: false,
                                branding: false
                              }}
                            />
                          </Box>
                        </TabPanel>

                        <TabPanel value={1}>
                          {/* HTML Source */}
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                            <Textarea
                              minRows={20}
                              maxRows={20}
                              sx={{
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                lineHeight: 1.4,
                                width: '100%',
                                '& textarea': { overflow: 'auto' }
                              }}
                              value={newsletterTemplate}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewsletterTemplate(e.target.value)}
                            />
                          </Box>
                        </TabPanel>

                        <TabPanel value={2}>
                          {/* Template Variables */}
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', maxHeight: 500, overflow: 'auto' }}>
                            <Typography level="title-md" mb={2}>
                              Available Template Variables
                            </Typography>
                            <Typography level="body-sm" color="neutral" mb={2}>
                              Use these variables in your newsletter template by wrapping them in double curly braces, e.g., {'{{customerName}}'}
                            </Typography>
                            <Table>
                              <thead>
                                <tr>
                                  <th>Variable</th>
                                  <th>Description</th>
                                  <th>Sample Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {newsletterTemplateVariables.map((variable) => (
                                  <tr key={variable.name}>
                                    <td>
                                      <code>{`{{${variable.name}}}`}</code>
                                    </td>
                                    <td>{variable.description}</td>
                                    <td>
                                      <code>{variable.sample}</code>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Box>
                        </TabPanel>

                        <TabPanel value={3}>
                          {/* Preview */}
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', maxHeight: 500, overflow: 'auto' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography level="title-md">Preview</Typography>
                              <Button
                                variant="outlined"
                                color="neutral"
                                onClick={() => {
                                  const previewWindow = window.open('', '_blank');
                                  if (previewWindow) {
                                    const previewContent = replaceNewsletterTemplateVariables(newsletterTemplate);
                                    previewWindow.document.write(`
                                      <!DOCTYPE html>
                                      <html>
                                      <head>
                                        <title>Newsletter Preview</title>
                                        <meta charset="utf-8">
                                      </head>
                                      <body>
                                        ${previewContent}
                                      </body>
                                      </html>
                                    `);
                                    previewWindow.document.close();
                                  }
                                }}
                                startDecorator={<IconWrapper icon={FaEye} />}
                                size="sm"
                              >
                                Open in New Window
                              </Button>
                            </Box>
                            <Box 
                              sx={{ 
                                p: 2, 
                                bgcolor: 'background.level1',
                                borderRadius: 'sm'
                              }}
                            >
                              <Typography level="title-md" mb={1}>
                                Subject: {replaceNewsletterTemplateVariables(newsletterSubject)}
                              </Typography>
                              <Divider sx={{ my: 2 }} />
                              <Box 
                                sx={{ 
                                  '& img': { maxWidth: '100%' },
                                  '& table': { 
                                    borderCollapse: 'collapse', 
                                    width: '100%',
                                    '& th, & td': { 
                                      border: '1px solid', 
                                      borderColor: 'divider', 
                                      p: 1 
                                    }
                                  }
                                }}
                              >
                                <div 
                                  dangerouslySetInnerHTML={{ 
                                    __html: replaceNewsletterTemplateVariables(newsletterTemplate) 
                                  }} 
                                />
                              </Box>
                            </Box>
                          </Box>
                        </TabPanel>
                      </Tabs>
                    </Box>
                  </Card>
                </Grid>

                {/* Recipients Management */}
                <Grid xs={12} md={4}>
                  <Card>
                    <Box sx={{ p: 3 }}>
                      <Typography level="title-lg" sx={{ mb: 2 }}>
                        Recipients
                        <Badge 
                          badgeContent={newsletterData.recipients.length} 
                          color="primary" 
                          sx={{ ml: 1 }}
                        />
                      </Typography>

                      {/* Recipients List */}
                      <Box sx={{ mb: 2 }}>
                        <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                          To ({newsletterData.recipients.length} emails)
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                          {newsletterData.recipients.length === 0 ? (
                            <Typography level="body-sm" color="neutral">
                              No recipients selected
                            </Typography>
                          ) : (
                            <Stack spacing={0.5}>
                              {newsletterData.recipients.map((email) => (
                                <Box
                                  key={email}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 1,
                                    bgcolor: 'background.level1',
                                    borderRadius: 'sm',
                                    '&:hover': {
                                      bgcolor: 'background.level2',
                                    }
                                  }}
                                >
                                  <Typography level="body-sm" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {email}
                                  </Typography>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="danger"
                                    onClick={() => handleRemoveRecipient(email)}
                                  >
                                    <IconWrapper icon={FaTimes} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Box>

                      {/* Excluded Recipients */}
                      {newsletterData.excludedEmails.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                            Excluded ({newsletterData.excludedEmails.length} emails)
                          </Typography>
                          <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                            <Stack spacing={0.5}>
                              {newsletterData.excludedEmails.map((email) => (
                                <Box
                                  key={email}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 1,
                                    bgcolor: 'background.level1',
                                    borderRadius: 'sm',
                                    opacity: 0.7,
                                    '&:hover': {
                                      bgcolor: 'background.level2',
                                    }
                                  }}
                                >
                                  <Typography level="body-sm" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {email}
                                  </Typography>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="primary"
                                    onClick={() => handleRestoreRecipient(email)}
                                  >
                                    <IconWrapper icon={FaPlus} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        </Box>
                      )}

                      {/* Unsubscribed Recipients */}
                      <Box sx={{ mb: 2 }}>
                        <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                          Unsubscribed ({unsubscribedEmails.length} emails)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                          Maintain this exclusion list manually, plus automatic opt-outs from unsubscribe links.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
                          <Input
                            value={manualUnsubscribeEmail}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualUnsubscribeEmail(e.target.value)}
                            placeholder="Add email to exclude (e.g. user@example.com)"
                            size="sm"
                          />
                          <Button
                            size="sm"
                            variant="outlined"
                            onClick={handleAddManualUnsubscribe}
                            loading={manualUnsubscribeSaving}
                          >
                            Add Exclusion
                          </Button>
                        </Stack>
                        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                          {unsubscribedEmails.length === 0 ? (
                            <Typography level="body-sm" color="neutral">
                              No unsubscribed emails yet
                            </Typography>
                          ) : (
                            <Stack spacing={0.5}>
                              {unsubscribedEmails.map((entry) => (
                                <Box
                                  key={entry.email}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    bgcolor: 'background.level1',
                                    borderRadius: 'sm',
                                    opacity: 0.8
                                  }}
                                >
                                  <Typography level="body-sm" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {entry.email}
                                  </Typography>
                                  <Chip size="sm" variant="soft" color={entry.source === 'manual_directory' ? 'warning' : 'primary'}>
                                    {entry.source === 'manual_directory' ? 'Manual' : 'Unsubscribe Link'}
                                  </Chip>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Box>

                      {/* Actions */}
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="sm"
                          onClick={setupNewsletterRecipients}
                          disabled={newsletterLoading}
                        >
                          Reset to All Customers
                        </Button>
                        <Typography level="body-xs" color="neutral" sx={{ textAlign: 'center' }}>
                          {newsletterData.recipients.length} of {customers.filter(c => c.email).length} customers selected
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={3} sx={{ p: 0 }}>
          <AccountsTab onCountChange={setAccountsCount} />
        </TabPanel>

      </Tabs>

      <Modal open={showOverwriteDialog} onClose={() => { setShowOverwriteDialog(false); setPendingAfterSave(null); }}>
        <ModalDialog>
          <DialogTitle>Clone this newsletter first</DialogTitle>
          <DialogContent>
            <Typography level="body-md">
              Clone {newsletterName || 'this newsletter'} first if it should stay as its own item. Saving overwrites {newsletterName || 'the current newsletter'}.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => { setShowOverwriteDialog(false); setPendingAfterSave(null); }}
              disabled={savingNewsletter}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleOverwriteNewsletter}
              disabled={savingNewsletter || !newsletterName.trim()}
              startDecorator={savingNewsletter ? <CircularProgress size="sm" /> : <IconWrapper icon={FaSave} />}
            >
              Overwrite
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={openCloneDialog}
              disabled={savingNewsletter}
              startDecorator={<IconWrapper icon={FaClone} />}
            >
              Clone first
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={showCloneDialog} onClose={() => setShowCloneDialog(false)}>
        <ModalDialog>
          <DialogTitle>Clone newsletter</DialogTitle>
          <DialogContent>
            <Typography level="body-md" sx={{ mb: 2 }}>
              This creates a new newsletter from the current content. {newsletterName || 'The original'} stays as it is.
            </Typography>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>New name</FormLabel>
              <Input
                value={cloneName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCloneName(e.target.value)}
                placeholder="The Residence Collection copy"
                size="md"
                autoFocus
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setShowCloneDialog(false)} disabled={savingNewsletter}>
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleCloneNewsletter}
              disabled={savingNewsletter || !cloneName.trim()}
              startDecorator={savingNewsletter ? <CircularProgress size="sm" /> : <IconWrapper icon={FaClone} />}
            >
              {savingNewsletter ? 'Cloning...' : 'Clone'}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={showCreateDialog} onClose={() => { setShowCreateDialog(false); setPendingAfterSave(null); }}>
        <ModalDialog>
          <DialogTitle>Name this newsletter</DialogTitle>
          <DialogContent>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Name</FormLabel>
              <Input
                value={createName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateName(e.target.value)}
                placeholder="The Residence Collection"
                size="md"
                autoFocus
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => { setShowCreateDialog(false); setPendingAfterSave(null); }}
              disabled={savingNewsletter}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleCreateNewsletter}
              disabled={savingNewsletter || !createName.trim()}
              startDecorator={savingNewsletter ? <CircularProgress size="sm" /> : <IconWrapper icon={FaSave} />}
            >
              {savingNewsletter ? 'Saving...' : 'Create'}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Send Newsletter Confirmation Dialog */}
      <Modal open={showSendNewsletterDialog} onClose={() => setShowSendNewsletterDialog(false)}>
        <ModalDialog>
          <DialogTitle>Confirm Newsletter Send</DialogTitle>
          <DialogContent>
            <Typography level="body-md">
              Are you sure you want to send this newsletter to <strong>{newsletterData.recipients.length} recipients</strong>?
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              Subject: {newsletterSubject || 'No subject'}
            </Typography>
            <Typography level="body-sm" color="neutral">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setShowSendNewsletterDialog(false)}
              disabled={newsletterLoading}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSendNewsletter}
              disabled={newsletterLoading}
              startDecorator={newsletterLoading ? <CircularProgress size="sm" /> : <IconWrapper icon={FaPaperPlane} />}
            >
              {newsletterLoading ? 'Sending...' : 'Send Newsletter'}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Customer Edit Form */}
      <CustomerEditForm
        customer={editingCustomer}
        open={!!editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSave={handleEditCustomer}
      />
    </Box>
  );
};

export default DirectoryPage;